import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { universityApi } from "@/lib/universityApi";
import { formatApiError } from "@/lib/formatError";
import { Loader2, CheckCircle2, ChevronLeft, ChevronRight, Award, Download, FileText } from "lucide-react";
import { UniHeader, LessonContent } from "./_lmsShared";
import KapeLessonMedia from "./_kapeLessonMedia";

export default function UniversityLesson() {
  const { lessonId } = useParams();
  const [params] = useSearchParams();
  const enrollmentId = params.get("enrollment_id");
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    if (!enrollmentId) return;
    (async () => {
      setLoading(true);
      try {
        const r = await universityApi.lmsLesson(lessonId, enrollmentId);
        setData(r.data);
      } catch (e) {
        toast.error(formatApiError(e, "Could not load lesson"));
      } finally {
        setLoading(false);
      }
    })();
  }, [lessonId, enrollmentId]);

  const onComplete = async () => {
    if (!enrollmentId) return;
    setCompleting(true);
    try {
      const r = await universityApi.lmsLessonComplete(lessonId, enrollmentId);
      if (r.data?.course_completed) toast.success("Course complete! Credential will be issued shortly.");
      else toast.success("Lesson marked complete.");
      // Refresh
      const fresh = await universityApi.lmsLesson(lessonId, enrollmentId);
      setData(fresh.data);
    } catch (e) {
      toast.error(formatApiError(e, "Could not mark complete"));
    } finally {
      setCompleting(false);
    }
  };

  if (!enrollmentId) return <div className="min-h-screen bg-[#FBF8F2] p-10">Missing enrollment_id parameter.</div>;
  if (loading) return <div className="min-h-screen bg-[#FBF8F2] p-10 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading lesson…</div>;
  if (!data) return <div className="min-h-screen bg-[#FBF8F2] p-10">Lesson not available.</div>;

  const l = data.lesson;
  const backCourse = `/university/courses/${enrollmentId}/${encodeURIComponent(l.course_code)}`;

  return (
    <div className="min-h-screen bg-[#FBF8F2]">
      <UniHeader title="Lesson" back={backCourse} />
      <main className="max-w-3xl mx-auto px-6 py-10" data-testid="uni-lesson-page">
        <div className="flex items-center gap-3 text-xs uppercase tracking-wide text-slate-500 mb-1">
          <FileText className="w-3.5 h-3.5" /><span className="font-mono">{l.course_code}</span>
          {data.completed && <span className="ml-2 inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" />Completed</span>}
        </div>
        <h1 className="font-display text-3xl text-slate-900" data-testid="uni-lesson-title">{l.title}</h1>
        {l.duration_minutes ? <div className="text-xs text-slate-500 mt-1">{l.duration_minutes} minutes</div> : null}

        <article className="mt-6 rounded-2xl border border-slate-200 bg-white p-6">
          <LessonContent content={l.content_md} />
          {l.video_url && (
            <div className="mt-4 text-xs">
              <a className="text-purple-900 hover:underline" href={l.video_url} target="_blank" rel="noreferrer">Open video in new tab →</a>
            </div>
          )}
        </article>

        {/* Phase 7B — Inline slide viewer + ElevenLabs narration player for KAPE-authored lessons */}
        <KapeLessonMedia lesson={l} enrollmentId={enrollmentId} />

        {(l.resources || []).length > 0 && (
          <section className="mt-5 rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">Downloadable resources</div>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {l.resources.map((r, i) => (
                <li key={i}>
                  <a className="flex items-center gap-2 rounded-xl border border-slate-100 px-3 py-2 text-sm hover:border-purple-200" href={r.url} target="_blank" rel="noreferrer" data-testid={`uni-lesson-resource-${i}`}>
                    <Download className="w-4 h-4 text-purple-900" />{r.label || r.url}
                  </a>
                </li>
              ))}
            </ul>
          </section>
        )}

        {l.quiz_id && (
          <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 flex items-center justify-between gap-3" data-testid="uni-lesson-quiz-cta">
            <div>
              <div className="font-medium">A short quiz is required for this lesson.</div>
              <div className="text-xs text-amber-800/80 mt-0.5">Pass the quiz before marking complete.</div>
            </div>
            <Link to={`/university/quizzes/${l.quiz_id}?enrollment_id=${enrollmentId}`} className="rounded-xl bg-amber-900 hover:bg-amber-800 text-white text-xs font-medium px-3 py-2">Open quiz →</Link>
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-3">
          {!data.completed ? (
            <button
              type="button"
              onClick={onComplete}
              disabled={completing}
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 hover:bg-emerald-600 text-white text-sm font-medium px-4 h-10 disabled:opacity-60"
              data-testid="uni-lesson-complete-btn"
            >
              {completing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Mark lesson complete
            </button>
          ) : (
            <div className="text-sm text-emerald-700 inline-flex items-center gap-2"><Award className="w-4 h-4" />Lesson completed.</div>
          )}
          <div className="ml-auto flex items-center gap-2">
            {data.prev_lesson && (
              <Link className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900" to={`/university/lessons/${data.prev_lesson.id}?enrollment_id=${enrollmentId}`} data-testid="uni-lesson-prev">
                <ChevronLeft className="w-4 h-4" />Previous
              </Link>
            )}
            {data.next_lesson && (
              <Link className="inline-flex items-center gap-1 text-sm text-purple-900 hover:underline" to={`/university/lessons/${data.next_lesson.id}?enrollment_id=${enrollmentId}`} data-testid="uni-lesson-next">
                Next<ChevronRight className="w-4 h-4" />
              </Link>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
